import { useDatabase } from "@nozbe/watermelondb/hooks";
import { useTranslation } from "../../../../utilities/i18n";
import {
  Card,
  FlatActionList,
  Text,
  Page,
  Stack,
} from "@towersystems/roam-common-native";
import Location from "../../../../utilities/data/model/Location";
import { Q } from "@nozbe/watermelondb";
import { useGenericFilterListScreenProvider } from "../../../../components/GenericFilterListScreen";
import React from "react";
import { InvoiceArrivalFilterValues } from "../../types";
import { useListFilterContext } from "../../../../utilities/list-filter/use-list-filter-context";
import {
  InvoiceArrivalFilterRootParamList,
  InvoiceArrivalRootParamList,
} from "../../../../types";
import { StackScreenProps } from "@react-navigation/stack";
import Supplier from "../../../../utilities/data/model/Supplier";

export type InvoiceArrivalFilterScreenProps = StackScreenProps<
  InvoiceArrivalRootParamList & InvoiceArrivalFilterRootParamList,
  "InvoiceArrivalFilterScreen"
>;

export const InvoiceArrivalFilterScreen = ({
  navigation,
}: InvoiceArrivalFilterScreenProps) => {
  const db = useDatabase();
  const { t } = useTranslation("invoice_arrival");
  const { setScreenOptions } = useGenericFilterListScreenProvider();
  const { getFilterValues, setFilterValue, watch } =
    useListFilterContext<InvoiceArrivalFilterValues>();

  const { locationId = [], supplierId = [], productId = [] } = watch();

  const handleOnLocationFilterSelect = React.useCallback(async () => {
    const locations = await db.collections
      .get<Location>("locations")
      .query(Q.and(Q.where("deleted_at", Q.eq(null))));

    setScreenOptions({
      title: t("action.filter_sale_location.label"),
      options: locations.map((location) => ({
        id: location.id,
        name: location.name,
      })),
      initialSelectedOptions: getFilterValues("locationId") ?? [],
      onSelectOption: (ids) => setFilterValue("locationId", ids),
    });

    navigation.navigate("GenericFilterListScreen");
  }, []);

  const handleOnSupplierFilterSelect = React.useCallback(async () => {
    const suppliers = await db.collections
      .get<Supplier>("suppliers")
      .query(Q.and(Q.where("deleted_at", Q.eq(null))));

    setScreenOptions({
      title: t("action.filter_supplier.label"),
      options: suppliers.map((supplier) => ({
        id: supplier.id,
        name: supplier.name,
      })),
      initialSelectedOptions: supplierId,
      onSelectOption: (ids) => setFilterValue("supplierId", ids),
    });
    navigation.navigate("GenericFilterListScreen");
  }, [supplierId]);

  return (
    <Page
      title={t("select_invoicearrival_filter_title")}
      secondaryTitle={t("select_product_filter_subtitle")}
    >
      <Stack vertical>
        <Card>
          <FlatActionList
            items={[
              {
                key: "locationId",
                title: t("common:actions.filter_location.label"),
                subTitle: t("common:actions.filter_location.description"),
                onPress: handleOnLocationFilterSelect,
                isAction: (locationId.length || 0) > 0,
                componentRight: (icon) => {
                  return (locationId.length || 0) > 0 ? (
                    <Stack centerVertical>
                      <Text color="action">{locationId.length} selected</Text>
                      {icon}
                    </Stack>
                  ) : (
                    icon
                  );
                },
              },
              {
                key: "suppliers",
                title: t("common:actions.filter_supplier.label"),
                subTitle: t("common:actions.filter_supplier.description"),
                isAction: supplierId.length > 0,
                onPress: handleOnSupplierFilterSelect,
                componentRight: (icon) => {
                  return supplierId.length > 0 ? (
                    <Stack centerVertical>
                      <Text color="action">{supplierId.length} selected</Text>
                      {icon}
                    </Stack>
                  ) : (
                    icon
                  );
                },
              },
              {
                key: "productId",
                title: t("common:actions.filter_product.label"),
                subTitle: t("common:actions.filter_product.description"),
                onPress: () =>
                  navigation.navigate("SelectableProductsFilterScreen"),
                isAction: (productId?.length || 0) > 0,
                componentRight: (icon) => {
                  return (productId?.length || 0) > 0 ? (
                    <Stack centerVertical>
                      <Text color="action">{productId?.length} selected</Text>
                      {icon}
                    </Stack>
                  ) : (
                    icon
                  );
                },
              },
            ]}
          />
        </Card>
      </Stack>
    </Page>
  );
};
